import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Box,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface CreateShareDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  buildingId: number;
  leaseIds: string[];
}

// Get the API base URL from environment
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
    return 'http://127.0.0.1:5001/urbanmix-dev/us-central1/api';
  }
  // Use the VITE_API_SERVER environment variable for production
  const apiServer = import.meta.env.VITE_API_SERVER;
  if (apiServer) {
    return `${apiServer}/api`;
  }
  return '/api';
};

export function CreateShareDialog({
  open,
  onClose,
  projectId,
  buildingId,
  leaseIds,
}: CreateShareDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);

    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${getApiBaseUrl()}/shares/pricing-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId,
          buildingId,
          leaseIds,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          expiresInDays: expiresInDays || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create share link');
      }

      const share = await response.json();

      // Construct the share URL
      const baseUrl = window.location.origin;
      setShareUrl(`${baseUrl}/review/${share.id}`);
    } catch (err) {
      console.error('Error creating share:', err);
      setError(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    // Reset state
    setTitle('');
    setDescription('');
    setExpiresInDays('');
    setError(null);
    setShareUrl(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ShareIcon />
          Share for External Review
        </Box>
      </DialogTitle>

      <DialogContent>
        {!shareUrl ? (
          <>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Create a shareable link that allows external reviewers to view and
              approve the {leaseIds.length} draft lease{leaseIds.length !== 1 ? 's' : ''}{' '}
              without needing to log in.
            </Typography>

            {error && (
              <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              className="mb-4"
              placeholder="e.g., Q1 2025 Renewals Review"
              helperText="A title to help reviewers identify this review"
            />

            <TextField
              label="Message for Reviewers (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              className="mb-4"
              placeholder="e.g., Please review and approve these proposed renewal terms."
            />

            <FormControl fullWidth>
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={expiresInDays}
                onChange={(e) =>
                  setExpiresInDays(e.target.value as number | '')
                }
                label="Link Expiration"
              >
                <MenuItem value="">Never expires</MenuItem>
                <MenuItem value={7}>Expires in 7 days</MenuItem>
                <MenuItem value={14}>Expires in 14 days</MenuItem>
                <MenuItem value={30}>Expires in 30 days</MenuItem>
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            <Alert severity="success" className="mb-4">
              Share link created successfully!
            </Alert>

            <Typography variant="body2" color="textSecondary" className="mb-2">
              Copy this link and send it to your reviewer:
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              gap={1}
              className="p-3 bg-zinc-800 rounded"
            >
              <TextField
                value={shareUrl}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              className="mt-2"
            >
              {expiresInDays
                ? `This link will expire in ${expiresInDays} days.`
                : 'This link will not expire unless you revoke it.'}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!shareUrl ? (
          <>
            <Button onClick={handleClose} disabled={creating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || leaseIds.length === 0}
              startIcon={
                creating ? <CircularProgress size={16} /> : <ShareIcon />
              }
            >
              {creating ? 'Creating...' : 'Create Share Link'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
